import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { QrCodeIcon, Check, AlertCircle, Calendar, CreditCard, ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function Subscription() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [canceling, setCanceling] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    setLoading(false);
  }, [user, navigate]);

  const plans = [
    {
      name: "Free",
      monthlyPrice: 0,
      yearlyPrice: 0,
      qrCodes: 10,
      messages: 10,
      contacts: 1,
      sms: false,
      features: [
        "10 QR codes per month",
        "10 messages per month",
        "Email notifications only",
        "1 notification contact per QR",
        "Basic support",
        "Web dashboard access"
      ],
      color: "gray"
    },
    {
      name: "Basic",
      monthlyPrice: 2,
      yearlyPrice: 20,
      savingsPercent: 17,
      qrCodes: 50,
      messages: 100,
      contacts: 3,
      sms: true,
      features: [
        "50 QR codes per month",
        "100 messages per month",
        "Email & SMS notifications",
        "Up to 3 notification contacts per QR",
        "Priority email support",
        "Message history & analytics"
      ],
      color: "blue"
    },
    {
      name: "Premium",
      monthlyPrice: 5,
      yearlyPrice: 49,
      savingsPercent: 18,
      qrCodes: 200,
      messages: 200,
      contacts: 10,
      sms: true,
      features: [
        "200 QR codes per month",
        "200 messages per month",
        "Email & SMS notifications",
        "Up to 10 notification contacts per QR",
        "Premium support",
        "Advanced analytics dashboard",
        "Custom branding options"
      ],
      color: "purple"
    }
  ];

  const currentPlan = plans.find(p => p.name.toLowerCase() === (user?.subscription_plan || 'free'));
  const currentPlanPrice = billingCycle === 'monthly' ? currentPlan?.monthlyPrice : currentPlan?.yearlyPrice;

  const handlePlanChange = async (planName) => {
    if (!user) return;
    
    const newPlan = plans.find(p => p.name === planName);
    const planPrice = billingCycle === 'monthly' ? newPlan.monthlyPrice : newPlan.yearlyPrice;

    // If downgrading to free
    if (planName === 'Free') {
      if (window.confirm('Are you sure you want to downgrade to the Free plan? This will take effect at the end of your current billing period.')) {
        try {
          await api.cancelSubscription();
          toast.success('Your subscription will be canceled at the end of the current billing period.');
          await refreshUser();
        } catch (error) {
          console.error('Cancellation failed:', error);
          toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
        }
      }
      return;
    }

    // If same plan, do nothing
    if (planName.toLowerCase() === user.subscription_plan) {
      toast.info('You are already on this plan');
      return;
    }

    setSelectedPlan(planName);
    setProcessing(true);

    try {
      // TODO: When Stripe is set up, this will create a checkout session
      // For now, we'll use the mock upgrade endpoint
      const response = await api.upgradeSubscription(planName.toLowerCase());
      toast.success(`Successfully changed to ${planName} plan!`);
      await refreshUser();
      setProcessing(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Plan change failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to change plan. Please try again.');
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!user || user.subscription_plan === 'free') return;

    if (window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
      setCanceling(true);
      try {
        await api.cancelSubscription();
        toast.success('Your subscription has been canceled. You will have access until ' + 
          new Date(user.subscription_expires).toLocaleDateString());
        await refreshUser();
      } catch (error) {
        console.error('Cancellation failed:', error);
        toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
      } finally {
        setCanceling(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5" />
              <span>Back to Dashboard</span>
            </Link>
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Current Plan Overview */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Subscription Management</h1>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mt-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-blue-200 text-sm mb-1">Current Plan</p>
                <p className="text-2xl font-bold">{currentPlan?.name}</p>
              </div>
              <div>
                <p className="text-blue-200 text-sm mb-1">Usage This Month</p>
                <p className="text-lg">
                  {user?.qr_count || 0} / {currentPlan?.qrCodes} QR Codes
                </p>
                <p className="text-lg">
                  {user?.message_count || 0} / {currentPlan?.messages} Messages
                </p>
              </div>
              <div>
                <p className="text-blue-200 text-sm mb-1">
                  {user?.subscription_plan === 'free' ? 'Subscription Status' : 'Next Billing Date'}
                </p>
                <p className="text-lg">
                  {user?.subscription_plan === 'free' 
                    ? 'Active - Free Forever' 
                    : user?.subscription_expires 
                      ? new Date(user.subscription_expires).toLocaleDateString()
                      : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {user?.subscription_plan !== 'free' && (
            <div className="mt-4 flex items-center space-x-4">
              <CreditCard className="h-5 w-5" />
              <span className="text-sm">
                Current billing: ${currentPlanPrice}/{billingCycle === 'monthly' ? 'month' : 'year'}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Billing Cycle Toggle */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Change Your Plan</h2>
            <div className="flex items-center justify-center space-x-3">
              <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                  billingCycle === 'yearly' ? 'bg-green-500' : 'bg-gray-300'
                }`}
                data-testid="billing-toggle"
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
                Yearly
              </span>
              {billingCycle === 'yearly' && (
                <span className="ml-2 px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded-full">
                  Save up to 18%
                </span>
              )}
            </div>
          </div>

          {/* Plan Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {plans.map((plan, index) => {
              const isCurrentPlan = plan.name.toLowerCase() === user?.subscription_plan;
              const planPrice = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
              
              return (
                <div
                  key={index}
                  className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                    isCurrentPlan ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  {isCurrentPlan && (
                    <div className="bg-blue-500 text-white text-center py-2 px-4 text-sm font-semibold">
                      Current Plan
                    </div>
                  )}
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                    
                    {/* Price */}
                    <div className="mb-6">
                      {plan.name === 'Free' ? (
                        <div>
                          <span className="text-4xl font-bold text-gray-900">Free</span>
                          <span className="text-gray-600 text-lg ml-2">forever</span>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-baseline">
                            <span className="text-5xl font-bold text-gray-900">${planPrice}</span>
                            <span className="text-gray-600 text-lg ml-2">
                              /{billingCycle === 'monthly' ? 'month' : 'year'}
                            </span>
                          </div>
                          {billingCycle === 'yearly' && plan.savingsPercent && (
                            <div className="mt-2 text-sm text-green-600 font-semibold">
                              Save ${(plan.monthlyPrice * 12) - plan.yearlyPrice} per year ({plan.savingsPercent}% off)
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Action Button */}
                    <button
                      onClick={() => handlePlanChange(plan.name)}
                      disabled={isCurrentPlan || processing}
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                        isCurrentPlan
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : processing && selectedPlan === plan.name
                          ? 'bg-gray-300 text-gray-600 cursor-wait'
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105'
                      }`}
                    >
                      {processing && selectedPlan === plan.name ? (
                        <span className="flex items-center justify-center">
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Processing...
                        </span>
                      ) : isCurrentPlan ? (
                        'Current Plan'
                      ) : plan.monthlyPrice > (currentPlan?.monthlyPrice || 0) ? (
                        `Upgrade to ${plan.name}`
                      ) : (
                        `Change to ${plan.name}`
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cancel Subscription Section */}
          {user?.subscription_plan !== 'free' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start">
                <AlertCircle className="h-6 w-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancel Subscription</h3>
                  <p className="text-gray-700 mb-4">
                    If you cancel your subscription, you'll retain access to your current plan features until the end of your billing period 
                    ({user?.subscription_expires ? new Date(user.subscription_expires).toLocaleDateString() : 'N/A'}). 
                    After that, your account will automatically switch to the Free plan.
                  </p>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={canceling}
                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
                  >
                    {canceling ? (
                      <span className="flex items-center">
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Canceling...
                      </span>
                    ) : (
                      'Cancel Subscription'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
